import React from 'react';
import {
    Box,
    Paper,
    Skeleton,
    styled,
    ToggleButton,
    ToggleButtonGroup,
    toggleButtonGroupClasses,
    Tooltip,
    Typography
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { getAllAudiences } from 'store/audiences/audiencesActions';
import { differenceInCalendarDays, sub } from 'date-fns';
import { GridDateFilter } from 'lib/grid/GridDateFilter';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import { MainLayout } from 'layouts/MainLayout';
import { Row } from 'ui/Flex';
import { useCurrencyString } from 'lib/hooks/useCurrencyString';
import { Line } from 'react-chartjs-2';
import { Throbber } from 'ui/Throbber';
import { GridLocationFilter } from 'lib/grid/GridLocationFilter';
import { getAllLocations } from 'store/locations/locationsActions';
import { ErrorLayout } from 'layouts/ErrorLayout';
import { MuiModal } from 'ui/MuiModal';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    AudienceTotalsBox,
    AudienceTotalsBoxButton,
    AudienceTotalsBoxSkeleton
} from 'components/analytics/audiences/AudienceTotalsBox';
import {
    AUDIENCE_ANALYTICS_COLORS,
    AUDIENCE_ANALYTICS_DATE_RANGE_SHORTCUTS,
    AUDIENCE_MEASURE_CONFIG,
    EAudienceMeasureType,
    EAudienceTimeGranularity,
    formatAudienceAnalyticsMeasureValue,
    isAudienceAnalyticsMeasureType
} from 'components/analytics/audiences/model';
import { AudienceAnalyticsAudiencePicker } from 'components/analytics/audiences/AudienceAnalyticsAudiencePicker';
import { useAudienceAnalyticsAudiencePicker } from 'components/analytics/audiences/useAudienceAnalyticsAudiencePicker';
import { useAudienceAnalyticsQueries } from 'components/analytics/audiences/useAudienceAnalyticsQueries';
import { useAudienceAnalyticsChart } from 'components/analytics/audiences/useAudienceAnalyticsChart';
import { AudienceMeasureTotalBoxes } from 'components/analytics/audiences/AudienceTotalBoxes';

// FIXME: handle errors

const ActiveUsersTotalsBox = styled(AudienceTotalsBox)({
    marginLeft: 0
});

// TODO: make it a standard component?
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    marginRight: theme.spacing(3),
    [`& .${toggleButtonGroupClasses.grouped}`]: {
        margin: theme.spacing(0, 1),
        padding: theme.spacing(0.5, 1.5),
        border: 0,
        borderRadius: theme.shape.borderRadius,
        [`&.${toggleButtonGroupClasses.disabled}`]: {
            border: 0
        }
    },
    [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
        marginLeft: -1,
        borderLeft: '1px solid transparent'
    }
}));

const ChartSkeletonBackdrop = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: theme.palette.background.paper
}));

const ChartSkeleton = styled(Skeleton)(({ theme }) => ({
    width: '100%',
    height: '100%',
    borderRadius: theme.shape.borderRadius
}));

export const AudienceAnalytics = () => {
    const dispatch = useDispatch();
    const getCurrencyString = useCurrencyString();

    // controls/filters
    const [dateRange, setDateRange] = React.useState<DateRangePickerValue>({
        startDate: sub(new Date(), { months: 3, days: 1 }),
        endDate: sub(new Date(), { days: 1 })
    });
    const retroDateRange = React.useMemo<[Date, Date]>(() => {
        const difference = differenceInCalendarDays(dateRange.endDate, dateRange.startDate);
        return [sub(dateRange.startDate, { days: difference + 1 }), sub(dateRange.startDate, { days: 1 })];
    }, [dateRange.endDate, dateRange.startDate]);
    const datePoints = React.useMemo<[Date, Date, Date]>(
        () => [retroDateRange[0], dateRange.startDate, dateRange.endDate],
        [dateRange.endDate, dateRange.startDate, retroDateRange]
    );
    const handleDateRangeChange = React.useCallback((dateRange: DateRangePickerValue) => {
        // after particular user interactions mui date range picker can return only start date
        // we can either ignore such result or set end date as yesterday
        if (!dateRange.endDate) {
            setDateRange({ startDate: dateRange.startDate, endDate: sub(new Date(), { days: 1 }) });
        } else {
            setDateRange(dateRange);
        }
    }, []);
    const [selectedLocation, setSelectedLocation] = React.useState<string>();
    const handleLocationChange = React.useCallback((id: string) => {
        setSelectedLocation(id);
    }, []);
    const [timeGranularity, setTimeGranularity] = React.useState<EAudienceTimeGranularity>(
        EAudienceTimeGranularity.MONTH
    );
    const handleTimeGranularityChange = React.useCallback((_: never, newValue) => {
        if (!newValue) {
            return;
        }
        setTimeGranularity(newValue);
    }, []);

    const {
        audienceOptions,
        audienceSelectOpen,
        audiencesToCompare,
        audiencesConfig,
        handleAudienceSelectOpen,
        handleAudienceSelectClose,
        handleAudienceChangeSubmit
    } = useAudienceAnalyticsAudiencePicker(AUDIENCE_ANALYTICS_COLORS);

    // Measures
    const [selectedMeasure, setSelectedMeasure] = React.useState<EAudienceMeasureType>(
        EAudienceMeasureType.AVG_VALUE
    );
    const handleMeasureClick = React.useCallback((id: string) => {
        if (isAudienceAnalyticsMeasureType(id)) {
            setSelectedMeasure(id);
        }
    }, []);
    // ============

    const {
        error,
        measureValues,
        audienceMeasureValues,
        currentUsersActiveMeasure,
        audiencesDataLoading,
        activeUsersChartQueries,
        audienceChartQueries
    } = useAudienceAnalyticsQueries(
        selectedMeasure,
        selectedLocation,
        dateRange,
        retroDateRange,
        timeGranularity,
        audiencesToCompare
    );

    const { chartData, chartLoading, chartError, chartOptions } = useAudienceAnalyticsChart(
        activeUsersChartQueries,
        audienceChartQueries,
        selectedMeasure,
        audiencesConfig
    );

    React.useEffect(() => {
        getAllAudiences()(dispatch);
        getAllLocations()(dispatch);
    }, [dispatch]);

    const renderAudienceTotals = React.useCallback(
        (audienceId: string) => {
            if (audiencesDataLoading) {
                return (
                    <AudienceTotalsBoxSkeleton
                        key={`totals-loading-${audienceId}`}
                        subtitle={audiencesConfig[audienceId]?.title || 'Unknown'}
                        color={audiencesConfig[audienceId]?.color}
                    />
                );
            }
            return (
                <AudienceTotalsBox
                    key={`totals-${audienceId}`}
                    audienceId={audienceId}
                    title={formatAudienceAnalyticsMeasureValue(
                        audienceMeasureValues[audienceId]?.[selectedMeasure],
                        AUDIENCE_MEASURE_CONFIG[selectedMeasure].format,
                        getCurrencyString
                    )}
                    subtitle={audiencesConfig[audienceId]?.title || 'Unknown'}
                    color={audiencesConfig[audienceId]?.color}
                    onClick={handleAudienceSelectOpen}
                />
            );
        },
        [
            audiencesDataLoading,
            audienceMeasureValues,
            selectedMeasure,
            getCurrencyString,
            audiencesConfig,
            handleAudienceSelectOpen
        ]
    );

    if (error || chartError) {
        return <ErrorLayout title="Error loading audience analytics" fallbackUrl="/" fallbackTitle="Home" />;
    }
    return (
        <MainLayout pageName="Audience Analytics" pageDescription="Compare your audience segments" noScroll>
            <Box width="100%">
                <Row align="space-between">
                    <GridLocationFilter
                        value={selectedLocation}
                        onChange={handleLocationChange}
                        paper
                        size="small"
                    />
                    <Box minWidth="265px">
                        <GridDateFilter
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            shortcuts={AUDIENCE_ANALYTICS_DATE_RANGE_SHORTCUTS}
                            clearable={false}
                            label=""
                            paper
                            size="small"
                            fullWidth
                        />
                    </Box>
                </Row>
            </Box>
            <Box mt={3} width="100%">
                <AudienceMeasureTotalBoxes
                    config={AUDIENCE_MEASURE_CONFIG}
                    values={measureValues}
                    onMeasureClick={handleMeasureClick}
                    selectedMeasure={selectedMeasure}
                    datePoints={datePoints}
                />
            </Box>
            <Paper sx={{ p: 2, mt: 3, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                <Typography variant="h6" gutterBottom>
                    <b>Compare Active Users</b>
                    <Tooltip title="An active user is someone who has made an order or payment within the selected time period.">
                        <HelpOutlineIcon />
                    </Tooltip>{' '}
                    with your audiences:
                </Typography>
                <Row>
                    {!currentUsersActiveMeasure.loading && (
                        <ActiveUsersTotalsBox
                            key="active-audience-total"
                            color={AUDIENCE_ANALYTICS_COLORS[0]}
                            title={formatAudienceAnalyticsMeasureValue(
                                currentUsersActiveMeasure.value,
                                AUDIENCE_MEASURE_CONFIG[selectedMeasure].format,
                                getCurrencyString
                            )}
                            subtitle="Active Users"
                        />
                    )}
                    {currentUsersActiveMeasure.loading && (
                        <AudienceTotalsBoxSkeleton
                            key="active-audience-total"
                            subtitle="Active Users"
                            color={AUDIENCE_ANALYTICS_COLORS[0]}
                        />
                    )}
                    {audiencesToCompare?.map(renderAudienceTotals)}
                    {audiencesToCompare?.length < 4 && (
                        <AudienceTotalsBoxButton onClick={handleAudienceSelectOpen}>
                            <AddIcon sx={{ height: 30, width: 30, fontWeight: 500, color: 'grey.500' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.75 }}>
                                Add audience
                            </Typography>
                        </AudienceTotalsBoxButton>
                    )}
                </Row>
            </Paper>
            <Paper sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                <Row align="space-between">
                    <Typography variant="h6">View over time</Typography>
                    <StyledToggleButtonGroup
                        value={timeGranularity}
                        exclusive
                        onChange={handleTimeGranularityChange}
                        size="small"
                    >
                        <ToggleButton value={EAudienceTimeGranularity.MONTH}>Monthly</ToggleButton>
                        <ToggleButton value={EAudienceTimeGranularity.WEEK}>Weekly</ToggleButton>
                        <ToggleButton value={EAudienceTimeGranularity.DAY}>Daily</ToggleButton>
                    </StyledToggleButtonGroup>
                </Row>
                <Box mt={1} position="relative">
                    <Line
                        data={chartData}
                        fallbackContent={<Throbber />}
                        width={0}
                        height="400px"
                        options={chartOptions}
                    />
                    {chartLoading && (
                        <ChartSkeletonBackdrop>
                            <ChartSkeleton variant="rectangular" />
                        </ChartSkeletonBackdrop>
                    )}
                </Box>
            </Paper>
            <Box pb={3} />
            <MuiModal open={audienceSelectOpen}>
                <AudienceAnalyticsAudiencePicker
                    onClose={handleAudienceSelectClose}
                    options={audienceOptions}
                    onSubmit={handleAudienceChangeSubmit}
                    selectedAudiences={audiencesToCompare}
                />
            </MuiModal>
        </MainLayout>
    );
};
